import React from 'react';
import { styled } from 'react-free-style';

/**
 * Inline fallback for a project-detail tab whose data loader failed.
 *
 * Rendered in place of the tab content when the owning project view detected
 * that the tab's underlying API call did not succeed. Pairs with the full-page
 * ProjectNotFound fallback used when the project itself cannot be loaded.
 *
 * Props:
 *   title       - headline, e.g. "Couldn't load Auspices"
 *   message     - optional sub-copy, defaults to a generic retry hint
 *   onRetry     - optional handler; omits the retry button when not provided
 *   isRetrying  - disables the retry button and shows a spinner-friendly label
 *   icon        - optional FontAwesome icon class (defaults to warning triangle)
 */
const SectionError = ({
  styles,
  title,
  message = 'Something went wrong while loading this section. Please try again.',
  onRetry,
  isRetrying = false,
  icon = 'fa fa-exclamation-triangle',
}) => {
  return (
    <div className={styles.container} role="alert" data-testid="section-error">
      <i className={`${icon} ${styles.icon}`} aria-hidden="true" />
      <div className={styles.title}>{title}</div>
      <div className={styles.message}>{message}</div>
      {onRetry && (
        <button
          type="button"
          className={styles.retryButton}
          onClick={onRetry}
          disabled={isRetrying}
          data-testid="section-error-retry"
        >
          {isRetrying ? 'Retrying…' : 'Retry'}
        </button>
      )}
    </div>
  );
};

const withStyles = styled({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    textAlign: 'center',
  },
  icon: {
    fontSize: '36px',
    color: '#b58900',
    marginBottom: '12px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '6px',
  },
  message: {
    fontSize: '14px',
    color: '#555',
    maxWidth: '420px',
    marginBottom: '18px',
  },
  retryButton: {
    cursor: 'pointer',
    padding: '6px 18px',
    fontSize: '14px',
    border: '1px solid #333',
    background: 'white',
    textTransform: 'uppercase',
    '&:hover': {
      background: '#f5f5f5',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    },
    '&:focus': {
      outline: 'none',
    },
  },
});

export default withStyles(SectionError);
